import React, { useEffect, useMemo } from "react";
import { ButtonControl } from "spark-core-dx/components";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import { readValue } from "../resourceConfigWizardUtils";
import { InfoPair, LinkedStepHeader } from "./WizardStepCommon";

const SERVER_NAME = "convoke-client-mcp";
const PROTOCOL_VERSION = "2025-03-26";
const ROUTE_TEMPLATE = "/mcp/{clientShortName}";

const getText = (...values) =>
  values
    .map((value) => String(value ?? "").trim())
    .find(Boolean) || "";

const getHostedApiBaseUri = () => {
  if (typeof window === "undefined") return "";
  const config = window.configData || window.config || {};
  const apiUris = config.API_URIS || config.apiUris || {};
  const convokeUri = getText(
    apiUris.CONVOKE,
    apiUris.Convoke,
    apiUris.convoke,
    config.CONVOKE_API_URI,
    config.convokeApiUri
  );
  return convokeUri.replace(/\/api\/Convoke\/?$/i, "").replace(/\/+$/, "");
};

const normalizeRoutePart = (value) =>
  String(value ?? "")
    .trim()
    .replace(/[^A-Za-z0-9_-]+/g, "");

const readCustomField = (item, ...keys) => {
  const customFields = readValue(item, "CustomFields", "customFields") || {};
  return getText(...keys.map((key) => customFields?.[key] ?? customFields?.[`${key.charAt(0).toLowerCase()}${key.slice(1)}`]));
};

const isTruthy = (value) => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "true" || normalized === "1" || normalized === "yes";
};

const resolveClientShortName = ({ coreContext, wizard, values }) => normalizeRoutePart(getText(
  readValue(coreContext, "ClientShortCode", "clientShortCode"),
  readValue(wizard, "ClientShortCode", "clientShortCode"),
  readCustomField(readValue(wizard, "ResourceConfiguration", "resourceConfiguration"), "McpToolsClientShortName"),
  readValue(values, "McpToolsClientShortName", "clientShortName"),
  readValue(coreContext?.Profile, "ClientName", "clientName")
));

const buildToolsRoute = (clientShortName) =>
  clientShortName ? `/mcp/${clientShortName}` : ROUTE_TEMPLATE;

const buildToolsUrl = (clientShortName) => {
  const route = buildToolsRoute(clientShortName);
  const baseUri = getHostedApiBaseUri();
  return baseUri ? `${baseUri}${route}` : route;
};

const RouteCard = ({ title, children }) => (
  <section className="resource-wizard__panel client-mcp-tools-card">
    <span className="resource-wizard__eyebrow">{title}</span>
    {children}
  </section>
);

const ClientMcpToolsConfiguratorStep = ({ context, onSave, onContinue, registerStepController }) => {
  const { coreContext } = useResourceDomainContext();
  const { readOnly, saving, values = {}, wizard = {} } = context;
  const localValues = useMemo(() => ({
    enableClient: true,
    confirmationReason: values.confirmationReason || values.ConfirmationReason || "Enable Client MCP Tools from resource setup."
  }), [values.ConfirmationReason, values.confirmationReason]);

  const clientShortName = useMemo(
    () => resolveClientShortName({ coreContext, wizard, values: { ...values, ...localValues } }),
    [coreContext, localValues, values, wizard]
  );
  const route = buildToolsRoute(clientShortName);
  const routeUrl = buildToolsUrl(clientShortName);
  const currentConfiguration = useMemo(
    () => readValue(wizard, "ResourceConfiguration", "resourceConfiguration") || {},
    [wizard]
  );
  const routeEnabled = isTruthy(readCustomField(currentConfiguration, "McpToolsRouteEnabled", "McpToolsClientEnabled"));

  useEffect(() => {
    registerStepController?.({
      prepareSave: async (currentValues = {}) => ({
        ...currentValues,
        ...localValues,
        ClientMcpToolsRouteUrl: routeUrl,
        McpToolsClientEnabled: localValues.enableClient !== false,
        McpToolsServerName: SERVER_NAME,
        McpToolsProtocolVersion: PROTOCOL_VERSION
      }),
      prepareContinue: async (currentValues = {}) => {
        return {
          ...currentValues,
          ...localValues,
          ClientMcpToolsRouteUrl: routeUrl,
          McpToolsClientEnabled: localValues.enableClient !== false,
          McpToolsServerName: SERVER_NAME,
          McpToolsProtocolVersion: PROTOCOL_VERSION,
          ResourceConfigurationId: readValue(currentConfiguration, "_id", "id") || currentValues.ResourceConfigurationId || ""
        };
      },
      skipStepInputValidation: true
    });
    return () => registerStepController?.(null);
  }, [currentConfiguration, localValues, registerStepController, routeUrl]);

  return (
    <div className="resource-wizard__linked-step client-mcp-tools-setup">
      <LinkedStepHeader
        title="Client MCP Tools"
        description="Configure the general Convoke tools and knowledge MCP route for this client."
        countText={routeEnabled ? "Route configured" : "Route pending"}
        countClassName={routeEnabled ? "ready" : "blocked"}
      />

      <div className="client-mcp-tools-setup-grid">
        <RouteCard title="Route">
          <div className="resource-wizard__pattern-facts">
            <InfoPair label="Route mode" value="client" />
            <InfoPair label="Client short name" value={clientShortName || "Not resolved"} />
            <InfoPair label="MCP Server URL" value={route} />
            <InfoPair label="Server" value={SERVER_NAME} />
            <InfoPair label="MCP protocol" value={PROTOCOL_VERSION} />
            <InfoPair label="Auth" value="Bearer user token" />
          </div>
          <div className="client-mcp-tools-setup-note">
            Access uses the current Convoke bearer/user token for the general tools route.
          </div>
        </RouteCard>

        <RouteCard title="Setup">
          <div className="resource-wizard__pattern-facts">
            <InfoPair label="Client route" value={routeEnabled ? "Enabled" : "Enabled on completion"} />
            <InfoPair label="Resource type" value="Client MCP Tools" />
            <InfoPair label="Workspace" value="Available after setup" />
            <InfoPair label="Capability review" value="Available after setup" />
          </div>
          <div className="client-mcp-tools-setup-note">
            This step records the client MCP route settings. The route and workspace are enabled when setup is completed.
          </div>
        </RouteCard>
      </div>

      <div className="resource-wizard__linked-step-actions">
        <div />
        <div className="resource-wizard__linked-step-actions-right">
          <ButtonControl
            type="standard-small"
            className="resource-wizard__button resource-wizard__button--secondary"
            onClick={() => onSave?.()}
            disabled={readOnly || saving}
            loading={saving}
          >
            Save
          </ButtonControl>
          <ButtonControl
            type="ok-small"
            className="resource-wizard__button resource-wizard__button--primary"
            onClick={() => onContinue?.()}
            disabled={readOnly || saving}
            loading={saving}
          >
            Save & Continue
          </ButtonControl>
        </div>
      </div>
    </div>
  );
};

export default ClientMcpToolsConfiguratorStep;
